import { inject, Injectable } from "@angular/core";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceAsyncTypeaheadProvider, NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { getTranslation } from "../../../utils/i18n.utils";
import { SearchCategorySectionsDto } from "../dto/search-category-sections.dto";
import { CategorySections } from "../models/category-sections.model";
import { CategorySectionsApi } from "./category-sections.api";

interface CategorySectionsTypeaheadSearchOptions {
    distributorIds?: number[];
    ignoreIds?: number[];
}

@Injectable()
export class CategorySectionsTypeaheadApi extends NiceAsyncTypeaheadProvider<CategorySections> {
    private readonly categorySectionsApi = inject(CategorySectionsApi);

    public readonly resource = "category-sections";

    private readonly pageSize = 20;

    public override async getById(id: number): Promise<CategorySections> {
        return lastValueFrom(this.categorySectionsApi.getById(id));
    }

    public override search(
        searchQuery: string,
        page: number,
        searchOptions: CategorySectionsTypeaheadSearchOptions
    ): Observable<NiceAsyncTypeaheadSearchResult<CategorySections>> {
        const dto: SearchCategorySectionsDto = {
            page,
            pageSize: this.pageSize,
            searchQuery
        };

        if (isPropertyDefined(searchOptions, "distributorIds")) {
            dto.distributorIds = searchOptions.distributorIds;
        }

        if (isPropertyDefined(searchOptions, "ignoreIds")) {
            dto.ignoreIds = searchOptions.ignoreIds;
        }

        return this.categorySectionsApi.search(dto).pipe(
            map((items) => ({
                items,
                nextPage: items.length === this.pageSize ? page + 1 : null
            }))
        );
    }

    public override format(item: CategorySections): string {
        if (!isPropertyDefined(item, "name")) {
            return "";
        }

        if (typeof item.name === "string") {
            return item.name;
        }

        return getTranslation(item.name) ?? "";
    }
}
